import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { FolderKanban, Plus, Trash2, ListPlus } from "lucide-react";

const STATUS = {
  active: { label: "Ativo", cls: "bg-emerald-500/20 text-emerald-600" },
  on_hold: { label: "Em Pausa", cls: "bg-amber-500/20 text-amber-600" },
  completed: { label: "Concluído", cls: "bg-blue-500/20 text-blue-500" },
  cancelled: { label: "Cancelado", cls: "bg-red-500/20 text-red-500" },
};

export default function ProjectsPage() {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState(false);
  const [entriesModal, setEntriesModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ name: "", client: "", budget: "", start_date: "", end_date: "" });
  const [entryForm, setEntryForm] = useState({ type: "cost", amount: "", description: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects`, { headers, withCredentials: true });
      setProjects(res.data);
    } catch { toast.error("Erro ao carregar projetos"); }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/projects`, {
        ...form,
        budget: toBaseCurrency(form.budget),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      }, { headers, withCredentials: true });
      toast.success("Projeto criado");
      setModal(false); setForm({ name: "", client: "", budget: "", start_date: "", end_date: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateStatus = async (p, status) => {
    try {
      await axios.put(`${API_URL}/projects/${p.project_id}/status?status=${status}`, {}, { headers, withCredentials: true });
      fetchAll();
    } catch { toast.error("Erro ao atualizar"); }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Remover projeto "${p.name}"?`)) return;
    await axios.delete(`${API_URL}/projects/${p.project_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const openEntries = async (p) => {
    try {
      const res = await axios.get(`${API_URL}/projects/${p.project_id}/entries`, { headers, withCredentials: true });
      setEntries(res.data);
      setSelected(p);
      setEntriesModal(true);
    } catch { toast.error("Erro ao carregar lançamentos"); }
  };

  const addEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/projects/${selected.project_id}/entries`, {
        ...entryForm,
        amount: toBaseCurrency(entryForm.amount),
      }, { headers, withCredentials: true });
      toast.success("Lançamento registado");
      setEntryForm({ type: "cost", amount: "", description: "" });
      const res = await axios.get(`${API_URL}/projects/${selected.project_id}/entries`, { headers, withCredentials: true });
      setEntries(res.data);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Projetos</h1>
          <p className="text-muted-foreground">Custos, receitas e rentabilidade por projeto</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-project-btn"><Plus className="w-4 h-4 mr-2" /> Novo Projeto</Button>
      </div>

      {projects.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><FolderKanban className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhum projeto registado</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {projects.map((p) => (
            <Card key={p.project_id} data-testid={`project-${p.project_id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.client}{p.start_date ? ` · ${p.start_date}` : ""}{p.end_date ? ` → ${p.end_date}` : ""}</p>
                  </div>
                  <Select value={p.status} onValueChange={(v) => updateStatus(p, v)}>
                    <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([v, s]) => <SelectItem key={v} value={v}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-xs text-muted-foreground">Custos</p><p className="font-mono font-medium text-red-500">{formatCurrency(p.total_costs)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Receitas</p><p className="font-mono font-medium text-emerald-600">{formatCurrency(p.total_revenues)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Resultado</p><p className={`font-mono font-bold ${p.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(p.profit)}</p></div>
                </div>
                {p.budget > 0 && (
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Orçamento: {formatCurrency(p.budget)}</span>
                      <span>{p.budget_used_pct}% usado</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${p.budget_used_pct > 100 ? "bg-red-500" : "bg-primary"}`} style={{ width: `${Math.min(p.budget_used_pct || 0, 100)}%` }} />
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEntries(p)} data-testid={`project-entries-${p.project_id}`}>
                    <ListPlus className="w-4 h-4 mr-1" /> Lançamentos
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteProject(p)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Projeto</DialogTitle></DialogHeader>
          <form onSubmit={createProject} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" /></div>
            <div className="space-y-1"><Label>Cliente</Label><Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} /></div>
            <div className="space-y-1"><Label>Orçamento</Label><Input type="number" step="0.01" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="project-budget-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Início</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Fim</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-project-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={entriesModal} onOpenChange={setEntriesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Lançamentos — {selected?.name}</DialogTitle></DialogHeader>
          <form onSubmit={addEntry} className="flex gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <Select value={entryForm.type} onValueChange={(v) => setEntryForm({ ...entryForm, type: v })}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost">Custo</SelectItem>
                  <SelectItem value="revenue">Receita</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1"><Label className="text-xs">Valor *</Label><Input required type="number" step="0.01" min="0.01" value={entryForm.amount} onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })} data-testid="entry-amount-input" /></div>
            <div className="space-y-1 flex-1"><Label className="text-xs">Descrição</Label><Input value={entryForm.description} onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })} /></div>
            <Button type="submit" size="sm" data-testid="add-entry-btn"><Plus className="w-4 h-4" /></Button>
          </form>
          <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
            {entries.map((e) => (
              <div key={e.entry_id} className="flex justify-between items-center p-2 rounded border text-sm">
                <div>
                  <Badge className={e.type === "cost" ? "bg-red-500/20 text-red-500" : "bg-emerald-500/20 text-emerald-600"}>{e.type === "cost" ? "Custo" : "Receita"}</Badge>
                  <span className="ml-2">{e.description}</span>
                </div>
                <p className="font-mono font-bold">{formatCurrency(e.amount)}</p>
              </div>
            ))}
            {entries.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sem lançamentos</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
